import { Link } from "@tanstack/react-router";
import {
  Users,
  Shield,
  FileText,
  Calculator,
  Megaphone,
  Monitor,
  Sparkles,
  UserCog,
  ChevronRight,
  Settings as SettingsIcon,
  Network,
  Trash2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuthStore } from "@shared/store/auth";

type Card = {
  titulo: string;
  descripcion: string;
  icon: LucideIcon;
  to?: string;
  proximamente?: boolean;
  roles?: string[];
};

type Grupo = { titulo: string; cards: Card[] };

const GRUPOS: Grupo[] = [
  {
    titulo: "Módulos de la aplicación",
    cards: [
      {
        titulo: "Recursos Humanos",
        descripcion: "Departamentos, política de vacaciones, fichaje/jornada y festivos.",
        icon: Users,
        to: "/settings/rrhh",
      },
      {
        titulo: "Facturación",
        descripcion: "Tipos de IVA, vencimiento, emisores, numeración y Verifacti.",
        icon: FileText,
        to: "/settings/billing",
      },
      {
        titulo: "Contabilidad",
        descripcion: "Ejercicio, cuentas por defecto y parámetros del modelo 303.",
        icon: Calculator,
        to: "/accounting/config",
      },
      {
        titulo: "Marketing",
        descripcion: "Reglas de lead scoring, etapas del pipeline y automatizaciones.",
        icon: Megaphone,
        to: "/marketing/scoring",
      },
      {
        titulo: "IT / Soporte",
        descripcion: "Categorías de tickets, prioridades y reglas de asignación.",
        icon: Monitor,
        proximamente: true,
      },
    ],
  },
  {
    titulo: "Sistema",
    cards: [
      {
        titulo: "Empresas y departamentos",
        descripcion: "Empresas, departamentos y usuarios relacionados entre sí.",
        icon: Network,
        to: "/settings/organization",
      },
      {
        titulo: "Equipo",
        descripcion: "Da de alta a tu equipo, asigna roles, activa cuentas y resetea contraseñas.",
        icon: UserCog,
        to: "/settings/team",
      },
      {
        titulo: "Roles y permisos",
        descripcion: "Define qué puede ver y hacer cada rol. Crea roles a medida.",
        icon: Shield,
        to: "/settings/roles",
      },
      {
        titulo: "Marca de la plataforma",
        descripcion: "Nombre, eslogan y logo que ve tu equipo en la aplicación.",
        icon: Sparkles,
        to: "/settings/branding",
      },
      {
        titulo: "Limpieza de datos",
        descripcion: "Elimina de forma controlada chats, justificaciones, fichajes y solicitudes que no disponen de borrado en sus pantallas habituales.",
        icon: Trash2,
        to: "/settings/data-cleanup",
        roles: ["super-admin", "admin", "rrhh", "admin_rrhh"],
      },
    ],
  },
];

export function ConfiguracionPage() {
  const hasRole = useAuthStore((state) => state.hasRole);

  return (
    <div className="p-8 h-full flex flex-col gap-8 min-h-0 overflow-auto">
      <header>
        <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
          <SettingsIcon size={28} className="text-brand-700" /> Configuración
        </h1>
        <p className="text-slate-500 mt-1">
          Administra cómo funciona cada apartado de tu instalación desde un único panel.
        </p>
      </header>

      {GRUPOS.map((grupo) => (
        <section key={grupo.titulo} className="flex flex-col gap-3">
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
            {grupo.titulo}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {grupo.cards.filter((s) => !s.roles || s.roles.some((role) => hasRole(role))).map((s) => {
              const Icon = s.icon;
              const inner = (
                <>
                  <div className="flex items-start justify-between">
                    <div
                      className={`w-12 h-12 rounded-2xl grid place-items-center ${
                        s.proximamente ? "bg-slate-100 text-slate-400" : "bg-brand-100 text-brand-700"
                      }`}
                    >
                      <Icon size={22} />
                    </div>
                    {s.proximamente ? (
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-1">
                        Próximamente
                      </span>
                    ) : (
                      <ChevronRight size={18} className="text-slate-300 group-hover:text-brand-600 transition" />
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="font-black text-slate-900">{s.titulo}</div>
                    <p className="mt-1 text-sm text-slate-500 leading-snug">{s.descripcion}</p>
                  </div>
                </>
              );

              const base = "block rounded-2xl border border-slate-200/70 bg-white p-5 shadow-soft transition";

              if (s.proximamente || !s.to) {
                return (
                  <div key={s.titulo} className={`${base} opacity-70`}>
                    {inner}
                  </div>
                );
              }
              return (
                <Link
                  key={s.titulo}
                  to={s.to}
                  className={`group ${base} hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md`}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

